// src/components/Welcome.js

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ShippingBanner from "./ShippingBanner";
import NavBar from "./NavBar";
import SocialLinks from "./SocialLinks";
import styles from "../style/Welcome.module.css";

const Welcome = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [hasEnded, setHasEnded] = useState(false);

  // Set language based on the QR code that was scanned.
  // English QR  ->  /welcome?lang=en
  // French QR   ->  /welcome?lang=fr
  useEffect(() => {
    const langParam = searchParams.get("lang");
    if (langParam === "fr") {
      i18n.changeLanguage("fr");
      localStorage.setItem("language", "fr");
    } else if (langParam === "en") {
      i18n.changeLanguage("en");
      localStorage.setItem("language", "en");
    }
    // If no lang param is present, keep whatever language is already set.
  }, [searchParams, i18n]);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
    if (!video.muted) {
      video.play().catch(() => {});
    }
  };

  const handleReplay = () => {
    const video = videoRef.current;
    if (!video) return;
    setHasEnded(false);
    video.currentTime = 0;
    video.play().catch(() => {});
  };

  const handleGoHome = () => {
    navigate("/");
  };

  return (
    <>
      <ShippingBanner />
      <NavBar />

      <div className={styles.welcomeLayout}>
        {/* LEFT column: mirrors the product page (empty thumbnail space + video),
            so the video sits in the exact same box as the product image. */}
        <div className={styles.leftColumn}>
          <div className={styles.thumbSpace} aria-hidden="true"></div>
          <div
            className={styles.videoWrap}
            onClick={hasEnded ? handleReplay : toggleMute}
          >
            <video
              ref={videoRef}
              className={styles.videoEl}
              src="/welcome.mp4"
              autoPlay
              muted
              playsInline
              controls={false}
              onEnded={() => setHasEnded(true)}
            />
            {!hasEnded && (
              <button
                type="button"
                className={styles.muteButton}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMute();
                }}
                aria-label={isMuted ? t("Unmute") : t("Mute")}
              >
                {isMuted ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M11 5L6 9H2v6h4l5 4V5z" fill="#fff" />
                    <path
                      d="M23 9l-6 6M17 9l6 6"
                      stroke="#fff"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M11 5L6 9H2v6h4l5 4V5z" fill="#fff" />
                    <path
                      d="M15.5 8.5a5 5 0 010 7M19 5a9 9 0 010 14"
                      stroke="#fff"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </button>
            )}

            {hasEnded && (
              <button
                type="button"
                className={styles.replayButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleReplay();
                }}
                aria-label={t("Replay")}
              >
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 5V2L7 6l5 4V7a5 5 0 11-5 5H5a7 7 0 107-7z"
                    fill="#fff"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* RIGHT column: welcome text, go home button, social links */}
        <div className={styles.contentSide}>
          <h1 className={styles.welcomeText}>{t("Welcome to Mcknight")}</h1>
          <button className={styles.goHomeButton} onClick={handleGoHome}>
            {t("Go Home")}
          </button>
          <div className={styles.socialWrap}>
            <SocialLinks />
          </div>
        </div>
      </div>
    </>
  );
};

export default Welcome;
